import {
	CaseModel,
	CaseReasonIds,
	DataGatheringId,
	DataGatheringRequiredResponse,
	DraftModel,
	DraftStatus,
	CaseStatus,
} from 'type-declarations';

export const getDraftRoute = (draft: DraftModel): string => {
	let componentPath = '';
	if (!draft?.status) return componentPath;

	switch (draft.status) {
		case DraftStatus.Incomplete:
		case DraftStatus.Reprocessing:
			componentPath = draft.precheckPassed ? `admin-data-gathering` : `precheck`;
			break;
		case DraftStatus.Pending:
			componentPath = `confirm-submission`;
			break;
		case DraftStatus.Unsupported:
			componentPath = `unsupported`;
			break;
		default:
			return componentPath;
	}

	const { organizationId, partnerCaseId } = draft;
	const draftRootPath = `organization/${organizationId}/draft/${partnerCaseId}`;
	return `${draftRootPath}/${componentPath}`;
};

export const getCaseRoute = (
	theCase: CaseModel,
	sdgData: DataGatheringRequiredResponse[]
): string => {
	let componentPath = '';

	if (sdgData && sdgData[0]?.dataGatheringScreen) {
		componentPath = DataGatheringIdToPathMap[sdgData[0].dataGatheringScreen];
	} else {
		const caseStatusToPathMap = getCaseStatusToPathMap(theCase);
		componentPath = caseStatusToPathMap[theCase.statusId];
	}

	const { organizationId, caseId } = theCase;
	const caseRootPath = `organization/${organizationId}/case/${caseId}`;
	if (!componentPath) {
		return caseRootPath; //routes to case status details page
	}
	return `${caseRootPath}/${componentPath}`;
};

const getClinicalNeededPath = (theCase: CaseModel): string => {
	const { transactionType, reasonId } = theCase;
	if (transactionType === 'INPATIENT_NOTICE_OF_ADMISSION') return 'noa';
	if (reasonId === CaseReasonIds.EXCEEDED_CLIN_SEND_ATT) return '';
	return 'clinical-required';
};

const getIncompletePath = (theCase: CaseModel): string =>
	theCase.reasonId === CaseReasonIds.PROVIDER_INFO_REQD
		? 'provider-info-required'
		: 'date-of-service-corrections';

const getCaseStatusToPathMap = (theCase: CaseModel): Record<CaseStatus, string> => {
	const StatusDetailsPagePath = '';
	return {
		[CaseStatus.APPROVED]: StatusDetailsPagePath,
		[CaseStatus.APPROVED_WITH_MODIFICATIONS]: StatusDetailsPagePath,
		[CaseStatus.CANCELED]: StatusDetailsPagePath,
		[CaseStatus.CLINICAL_NEEDED]: getClinicalNeededPath(theCase),
		[CaseStatus.CLINICAL_REQUESTED_PROVIDER_NOTIFIED]: StatusDetailsPagePath,
		[CaseStatus.DENIED]: StatusDetailsPagePath,
		[CaseStatus.IN_REVIEW]: StatusDetailsPagePath,
		[CaseStatus.INCOMPLETE]: getIncompletePath(theCase),
		[CaseStatus.MEMBER_NOT_COVERED]: StatusDetailsPagePath,
		[CaseStatus.MEMBER_NOT_SUPPORTED]: StatusDetailsPagePath,
		[CaseStatus.MULTIPLE_DECISIONS]: StatusDetailsPagePath,
		[CaseStatus.NOT_COVERED]: StatusDetailsPagePath,
		[CaseStatus.NOT_ELIGIBLE]: StatusDetailsPagePath,
		[CaseStatus.NOT_REQUIRED]: StatusDetailsPagePath,
		[CaseStatus.PARTIALLY_APPROVED]: StatusDetailsPagePath,
		[CaseStatus.PARTIALLY_REVIEWED]: StatusDetailsPagePath,
		[CaseStatus.PEER_TO_PEER]: StatusDetailsPagePath,
		[CaseStatus.PROCESSING]: StatusDetailsPagePath,
		[CaseStatus.SERVICE_NOT_HANDLED]: StatusDetailsPagePath,
		[CaseStatus.SURVEY_REQUIRED]: 'survey',
		[CaseStatus.UNABLE_TO_PROCEED]: StatusDetailsPagePath,
		[CaseStatus.WITHDRAWN]: StatusDetailsPagePath,
	};
};

const DataGatheringIdToPathMap: Record<DataGatheringId, string> = {
	[DataGatheringId.ProviderSelections]: 'sdg-provider-info-required',
	[DataGatheringId.ClinicalFileUpload]: 'sdg-clinical-required',
	[DataGatheringId.ClinicalUploadandFax]: 'sdg-clinical-required',
	[DataGatheringId.EnhancedProviderSelection]: 'sdg-enhanced-provider-selection-required',
	[DataGatheringId.NotificationOfAdmission]: 'noa',
	[DataGatheringId.ContinuedStayReview]: '',
	[DataGatheringId.ExternalActionRequired]: 'external-action-required',
	[DataGatheringId.FHIRClinicalAttestation]: 'sdg-fhir-clinical-attestation-required',
	[DataGatheringId.RetroRequestCorrection]: 'sdg-retro-request-correction',
	[DataGatheringId.SurveyUrl]: 'sdg-survey-url-required',
	[DataGatheringId.HtmlSurvey]: 'html-survey',
	[DataGatheringId.None]: '',
};
